import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Alert as MuiAlert,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import { AlertSubscriptionStatusLabel, MANUAL_SUBSCRIPTION_STATUS_OPTIONS, RefundedExternallyLabel, REFUNDED_EXTERNALLY_OPTIONS } from '../../types/alerts';
import FulfillmentStatusSelect from './FulfillmentStatusSelect';
import type { VerifiNotice } from '../../types/verifiNotice';

interface RdrResolutionDialogProps {
  open: boolean;
  notice: VerifiNotice;
  onClose: () => void;
  onSave: () => void;
}

export default function RdrResolutionDialog({
  open,
  notice,
  onClose,
  onSave,
}: RdrResolutionDialogProps) {
  const { post, loading, error } = useApi();
  const [status, setStatus] = useState<string>(notice.subscription_status);
  const [refundedExternally, setRefundedExternally] = useState<string>(notice.refunded_externally ?? '');
  const [fulfillmentStatus, setFulfillmentStatus] = useState<string>(notice.fulfillment_status ?? '');
  const [notes, setNotes] = useState<string>(notice.cm_notes ?? '');
  const [orderUrl, setOrderUrl] = useState<string>(notice.matched_order_url ?? '');

  // queued / auto_* are system-set and not manual options; show the current one
  // (disabled) so the dropdown reflects reality and manual overrides stay available.
  const currentStatusIsSystemSet = !(MANUAL_SUBSCRIPTION_STATUS_OPTIONS as readonly string[]).includes(notice.subscription_status);

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = async () => {
    if (loading) return;
    try {
      await post('/api/alerts/verifi-notices/resolution', {
        noticeId: notice.verifi_notice_id,
        subscriptionStatus: status,
        ...(refundedExternally && { refundedExternally }),
        fulfillmentStatus,
        notes: notes.trim() ? notes.trim() : undefined,
        orderUrl: orderUrl.trim() ? orderUrl.trim() : undefined,
      });
      onSave();
    } catch (err) {
      console.error('Failed to update resolution:', err);
    }
  };

  const isUnchanged =
    status === notice.subscription_status &&
    refundedExternally === (notice.refunded_externally ?? '') &&
    fulfillmentStatus === (notice.fulfillment_status ?? '') &&
    (notes ?? '') === (notice.cm_notes ?? '') &&
    orderUrl.trim() === (notice.matched_order_url ?? '');

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="rdr-resolution-dialog-title">
      <DialogTitle id="rdr-resolution-dialog-title">
        Edit Resolution — Notice #{notice.case_id}
      </DialogTitle>
      <DialogContent>
        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }} disabled={loading}>
              <InputLabel id="rdr-subscription-status-label" shrink>Subscription Status</InputLabel>
              <Select
                labelId="rdr-subscription-status-label"
                id="rdr-subscription-status"
                value={status}
                label="Subscription Status"
                notched
                onChange={(e) => setStatus(e.target.value)}
              >
                {currentStatusIsSystemSet && (
                  <MenuItem value={notice.subscription_status} disabled>
                    {AlertSubscriptionStatusLabel[notice.subscription_status] ?? notice.subscription_status}
                  </MenuItem>
                )}
                {MANUAL_SUBSCRIPTION_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {AlertSubscriptionStatusLabel[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }} disabled={loading}>
              <InputLabel id="rdr-refunded-externally-label" shrink>Refunded Externally</InputLabel>
              <Select
                labelId="rdr-refunded-externally-label"
                id="rdr-refunded-externally"
                value={refundedExternally}
                label="Refunded Externally"
                displayEmpty
                notched
                onChange={(e) => setRefundedExternally(e.target.value)}
              >
                {REFUNDED_EXTERNALLY_OPTIONS.map((v) => (
                  <MenuItem key={v} value={v}>{RefundedExternallyLabel[v]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FulfillmentStatusSelect
            value={fulfillmentStatus}
            onChange={setFulfillmentStatus}
            disabled={loading}
          />
          <TextField
            size="small"
            fullWidth
            label="Order URL"
            value={orderUrl}
            onChange={(e) => setOrderUrl(e.target.value)}
            placeholder="https://..."
            helperText="Shopify: https://<shop>.myshopify.com/admin/orders/<id> (admin.shopify.com links are converted). CheckoutChamp: https://crm.checkoutchamp.com/customer/cs/orders/?orderId=<id> (customer page links are rejected). This field is for tracking purposes only and does not mean the order will be refunded automatically."
            disabled={loading}
          />
          <TextField
            size="small"
            fullWidth
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            disabled={loading}
            placeholder="Optional context for this status change..."
            helperText="Reuses the notice's CM Notes field."
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || isUnchanged || !fulfillmentStatus}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
